import { formatDate } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { finalize } from 'rxjs/operators';
import {
  MyTeamCheckInStatus,
  MyTeamWeekRow,
  ReservationService,
} from 'src/app/Services/Reservation/reservation.service';
import { TableColumn, TableStatusTone } from '../table/table.component';

export type TeamViewTab = 'today' | 'thisWeek' | 'lastWeek';

export interface TeamWeekView {
  isLoading: boolean;
  loaded: boolean;
  days: string[];
  rows: MyTeamWeekRow[];
  rangeLabel: string;
  columns: TableColumn<MyTeamWeekRow>[];
  pagedRows: MyTeamWeekRow[];
  pageIndex: number;
  pageSize: number;
}

@Component({
  standalone: false,
  selector: 'app-my-team-view',
  templateUrl: './my-team-view.component.html',
  styleUrl: './my-team-view.component.scss',
})
export class MyTeamViewComponent implements OnInit {
  readonly teamListTooltip = 'Your direct reports with a booking in the last 30 days';
  readonly today = new Date();

  readonly statusLegendItems: { status: MyTeamCheckInStatus; description: string }[] = [
    { status: 'Checked In', description: 'Booked a workspace and checked in on site.' },
    { status: 'Booked', description: 'Has a booking but has not checked in yet.' },
    {
      status: 'Missed Check-In',
      description: 'Had a booking but did not check in before the deadline.',
    },
    { status: 'No Booking', description: 'Did not book a workspace for this day.' },
  ];

  selectedTabIndex = 0;
  showStatusInfo = false;

  // "This week" is hidden from the UI for now; its loading logic is kept so it can be re-enabled here.
  readonly showThisWeekTab = false;
  readonly visibleTabs: readonly TeamViewTab[] = this.showThisWeekTab
    ? ['today', 'thisWeek', 'lastWeek']
    : ['today', 'lastWeek'];

  isLoading = false;
  teamLoaded = false;
  teamRows: any[] = [];
  pageIndex = 0;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50];
  statistics = {
    confirmedOnSite: 0,
    noBooking: 0,
    booked: 0,
    missedCheckIns: 0,
  };

  readonly teamTableColumns: TableColumn[] = [
    { id: 'name', header: 'Name', showHeader: true, type: 'text', field: 'name' },
    { id: 'location', header: 'Location', showHeader: true, type: 'text', field: 'location' },
    { id: 'floor', header: 'Floor', showHeader: true, type: 'text', field: 'floor' },
    { id: 'deskNo', header: 'Desk No.', showHeader: true, type: 'text', field: 'deskNo' },
    {
      id: 'selectedTime',
      header: 'Selected time',
      showHeader: true,
      type: 'text',
      field: 'selectedTime',
    },
    {
      id: 'checkInStatus',
      header: 'Check-in status',
      showHeader: true,
      type: 'status',
      status: (row) =>
        row?.checkInStatus
          ? { label: row.checkInStatus, tone: this.getStatusTone(row.checkInStatus) }
          : null,
    },
  ];

  readonly teamRowId = (row: any): string =>
    `${row?.name ?? ''}|${row?.deskNo ?? ''}|${row?.selectedTime ?? ''}`;

  readonly weekRowId = (row: MyTeamWeekRow): number => row.userId;

  private readonly weekNameColumn: TableColumn<MyTeamWeekRow> = {
    id: 'name',
    header: 'Name',
    showHeader: true,
    type: 'text',
    field: 'name',
  };

  pagedTeamRows: any[] = [];

  thisWeek: TeamWeekView = this.createWeekView();
  lastWeek: TeamWeekView = this.createWeekView();

  constructor(
    private reservationService: ReservationService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  ngOnInit(): void {
    this.loadMyTeamLocations();
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;

    const tab = this.visibleTabs[index];
    if (tab === 'thisWeek') {
      this.loadWeek(this.thisWeek, this.today);
    } else if (tab === 'lastWeek') {
      const lastWeekDate = new Date(this.today);
      lastWeekDate.setDate(lastWeekDate.getDate() - 7);
      this.loadWeek(this.lastWeek, lastWeekDate);
    }
  }

  loadMyTeamLocations(): void {
    this.isLoading = true;

    this.reservationService
      .getMyTeamLocations()
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe((data) => {
        const response = data?.value;

        this.statistics = response?.statistics ?? this.statistics;
        this.teamRows = response?.team ?? [];
        this.teamLoaded = true;
        this.pageIndex = 0;
        this.updatePagedTeamRows();
        // Zoneless: the HTTP callback does not trigger change detection on its own.
        this.changeDetectorRef.markForCheck();
      });
  }

  loadWeek(view: TeamWeekView, date: Date): void {
    if (view.loaded || view.isLoading) {
      return;
    }

    view.isLoading = true;

    this.reservationService
      .getMyTeamWeek(formatDate(date, 'yyyy-MM-dd', this.locale))
      .pipe(
        finalize(() => {
          view.isLoading = false;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe((data) => {
        const response = data?.value;

        view.days = response?.days ?? [];
        view.rows = response?.team ?? [];
        view.rangeLabel = view.days.length
          ? this.formatRange(view.days[0], view.days[view.days.length - 1])
          : '';
        view.columns = this.buildWeekColumns(view.days);
        view.pageIndex = 0;
        this.updatePagedWeekRows(view);
        view.loaded = true;
        this.changeDetectorRef.markForCheck();
      });
  }

  onPageChanged(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedTeamRows();
  }

  private updatePagedTeamRows(): void {
    const start = this.pageIndex * this.pageSize;
    this.pagedTeamRows = this.teamRows.slice(start, start + this.pageSize);
  }

  onWeekPageChanged(view: TeamWeekView, event: PageEvent): void {
    view.pageIndex = event.pageIndex;
    view.pageSize = event.pageSize;
    this.updatePagedWeekRows(view);
  }

  noDataText(isLoading: boolean, loaded: boolean): string {
    if (isLoading) {
      return 'Loading...';
    }

    return loaded ? 'No team members to show.' : '';
  }

  private updatePagedWeekRows(view: TeamWeekView): void {
    const start = view.pageIndex * view.pageSize;
    view.pagedRows = view.rows.slice(start, start + view.pageSize);
  }

  private buildWeekColumns(days: string[]): TableColumn<MyTeamWeekRow>[] {
    return [
      this.weekNameColumn,
      ...days.map((day, index): TableColumn<MyTeamWeekRow> => ({
        id: `day-${index}`,
        header: formatDate(day, 'EEE, MMM d', this.locale),
        showHeader: true,
        type: 'statusIcon',
        align: 'center',
        columnWidth: 136,
        status: (row) => {
          const status = row.statuses[index];
          return status
            ? {
                label: status,
                tone: this.getStatusTone(status),
                icon: this.getDayStatusIcon(status),
              }
            : null;
        },
      })),
    ];
  }

  getStatusTone(status: string): TableStatusTone {
    switch (status) {
      case 'Checked In':
        return 'success';
      case 'Booked':
        return 'warning';
      case 'Missed Check-In':
        return 'error';
      default:
        return 'neutral';
    }
  }

  getDayStatusIcon(status: MyTeamCheckInStatus): string | null {
    switch (status) {
      case 'Checked In':
        return 'check';
      case 'Booked':
        return 'event_available';
      case 'Missed Check-In':
        return 'close';
      default:
        return null;
    }
  }

  private createWeekView(): TeamWeekView {
    return {
      isLoading: false,
      loaded: false,
      days: [],
      rows: [],
      rangeLabel: '',
      columns: [this.weekNameColumn],
      pagedRows: [],
      pageIndex: 0,
      pageSize: 10,
    };
  }

  private formatRange(start: string, end: string): string {
    return `${formatDate(start, 'd MMM y', this.locale)} - ${formatDate(end, 'd MMM y', this.locale)}`;
  }
}
